import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, XCircle, Loader2, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fields we consider when computing per-page coverage. Each is a column in the report.
const FIELDS = [
  { key: "meta_title", label: "Title" },
  { key: "meta_description", label: "Description" },
  { key: "h1", label: "H1" },
  { key: "canonical", label: "Canonical" },
  { key: "og_title", label: "OG Title" },
  { key: "og_image", label: "OG Image" },
  { key: "schema_jsonld", label: "Schema" },
];

function Cell({ filled }) {
  return filled ? (
    <CheckCircle2 className="w-4 h-4 text-emerald-600" data-testid="cell-filled" />
  ) : (
    <XCircle className="w-4 h-4 text-slate-300" data-testid="cell-empty" />
  );
}

/**
 * CoverageReport — shows all pages with a filled/empty badge per SEO field.
 * Lets Speicher (and the owner) see at a glance which pages still need edits.
 */
export default function CoverageReport({ authHeaders }) {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all"); // all | with | without

  useEffect(() => {
    axios
      .get(`${API}/seo/pages`, { headers: authHeaders() })
      .then((r) => setPages(r.data || []))
      .catch(() => setPages([]))
      .finally(() => setLoading(false));
  }, [authHeaders]);

  const rows = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return pages
      .map((p) => {
        const ov = p.override || {};
        const filled = FIELDS.map((f) => Boolean(ov[f.key] && String(ov[f.key]).trim()));
        const filledCount = filled.filter(Boolean).length;
        return { ...p, filled, filledCount, filledPct: Math.round((filledCount / FIELDS.length) * 100) };
      })
      .filter((r) => {
        if (ql && !r.path.toLowerCase().includes(ql) && !(r.title || "").toLowerCase().includes(ql)) return false;
        if (filter === "with" && r.filledCount === 0) return false;
        if (filter === "without" && r.filledCount > 0) return false;
        return true;
      });
  }, [pages, q, filter]);

  const totals = useMemo(() => {
    const withEdits = pages.filter((p) => {
      const ov = p.override || {};
      return FIELDS.some((f) => ov[f.key] && String(ov[f.key]).trim());
    }).length;
    return { total: pages.length, withEdits, without: pages.length - withEdits };
  }, [pages]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading coverage report…
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="coverage-report">
      {/* Header stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-xs text-slate-500">Total pages</CardTitle></CardHeader>
          <CardContent className="text-2xl font-bold text-[#0F172A]" data-testid="stat-total">{totals.total}</CardContent>
        </Card>
        <Card className="border-emerald-200 bg-emerald-50/40">
          <CardHeader className="pb-2"><CardTitle className="text-xs text-emerald-700">With overrides saved</CardTitle></CardHeader>
          <CardContent className="text-2xl font-bold text-emerald-700" data-testid="stat-with">{totals.withEdits}</CardContent>
        </Card>
        <Card className="border-amber-200 bg-amber-50/40">
          <CardHeader className="pb-2"><CardTitle className="text-xs text-amber-700">Not yet edited</CardTitle></CardHeader>
          <CardContent className="text-2xl font-bold text-amber-700" data-testid="stat-without">{totals.without}</CardContent>
        </Card>
      </div>

      {/* Filter controls */}
      <div className="flex flex-col md:flex-row gap-2 items-stretch md:items-center">
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by path or title…"
          className="md:max-w-xs"
          data-testid="coverage-search"
        />
        <div className="flex gap-1 flex-wrap">
          {[
            { v: "all", l: `All (${pages.length})` },
            { v: "with", l: `With edits (${totals.withEdits})` },
            { v: "without", l: `Not edited (${totals.without})` },
          ].map((b) => (
            <button
              type="button"
              key={b.v}
              onClick={() => setFilter(b.v)}
              className={`px-3 py-1.5 text-xs rounded-md border transition-colors ${
                filter === b.v
                  ? "bg-[#F97316] text-white border-[#F97316]"
                  : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
              }`}
              data-testid={`filter-${b.v}`}
            >
              {b.l}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="text-left px-3 py-2 font-semibold text-slate-700">Page</th>
                {FIELDS.map((f) => (
                  <th key={f.key} className="text-center px-2 py-2 font-semibold text-slate-700 whitespace-nowrap">
                    {f.label}
                  </th>
                ))}
                <th className="text-center px-3 py-2 font-semibold text-slate-700">Coverage</th>
                <th className="px-2 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={FIELDS.length + 3} className="text-center py-10 text-slate-400">
                    No pages match your filter.
                  </td>
                </tr>
              )}
              {rows.map((r) => (
                <tr key={r.path} className="border-b hover:bg-slate-50/70" data-testid={`row-${r.path}`}>
                  <td className="px-3 py-2 align-middle">
                    <div className="font-medium text-slate-800 text-sm">{r.title || r.path}</div>
                    <div className="font-mono text-[11px] text-slate-500">{r.path}</div>
                    {r.gated && (
                      <Badge variant="outline" className="mt-1 text-[10px] text-amber-700 border-amber-300">gated</Badge>
                    )}
                  </td>
                  {FIELDS.map((f, i) => (
                    <td key={f.key} className="text-center px-2 py-2 align-middle">
                      <div className="inline-flex"><Cell filled={r.filled[i]} /></div>
                    </td>
                  ))}
                  <td className="text-center px-3 py-2 align-middle">
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-16 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                        <div
                          className={`h-full ${r.filledPct === 0 ? "bg-slate-300" : r.filledPct < 50 ? "bg-amber-500" : "bg-emerald-500"}`}
                          style={{ width: `${r.filledPct}%` }}
                        />
                      </div>
                      <span className="text-xs text-slate-500 w-8 text-right">{r.filledPct}%</span>
                    </div>
                  </td>
                  <td className="px-2 py-2 align-middle text-right">
                    <a
                      href={`${window.location.origin}${r.path}?_seo_preview=${Date.now()}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-[#F97316] hover:underline"
                      title="Preview this page in a new tab"
                      data-testid={`preview-${r.path}`}
                    >
                      <ExternalLink className="w-3 h-3" /> Preview
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
